// src/components/CameraRequests/CameraRequestsList.jsx
import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Stack,
  Divider,
  Button
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  VideoLibrary as FootageIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useThemeSettings } from '../../contexts/ThemeContext';
import { useIsMobile } from '../../hooks/useResponsive';

const getStatusColor = (status) => {
  switch (status) {
    case 'submitted':
      return 'default';
    case 'pending_site_approval':
      return 'warning';
    case 'pending_dpo_approval':
      return 'info';
    case 'approved_pending_retrieval':
      return 'primary';
    case 'completed':
      return 'success';
    case 'rejected':
      return 'error';
    case 'closed_no_footage':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusLabel = (status, t) => {
  switch (status) {
    case 'submitted':
      return t ? t('CameraRequests.status_submitted') : 'Submitted';
    case 'pending_site_approval':
      return t ? t('CameraRequests.status_pending_site_approval') : 'Pending Site Manager';
    case 'pending_dpo_approval':
      return t ? t('CameraRequests.status_pending_dpo_approval') : 'Pending DPO';
    case 'approved_pending_retrieval':
      return t ? t('CameraRequests.status_approved_pending_retrieval') : 'Approved - Pending Footage';
    case 'completed':
      return t ? t('CameraRequests.status_completed') : 'Completed';
    case 'rejected':
      return t ? t('CameraRequests.status_rejected') : 'Rejected';
    case 'closed_no_footage':
      return t ? t('CameraRequests.status_closed_no_footage') : 'No Footage Found';
    default:
      return status;
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'submitted':
    case 'pending_site_approval':
    case 'pending_dpo_approval':
    case 'approved_pending_retrieval':
      return <PendingIcon fontSize="small" />;
    case 'completed':
      return <ApprovedIcon fontSize="small" />;
    case 'rejected':
    case 'closed_no_footage':
      return <RejectedIcon fontSize="small" />;
    default:
      return null;
  }
};

const CameraRequestsList = ({ requests = [], loading, onViewRequest, onDeleteRequest, isAdmin, onRefresh }) => {
  const { t } = useThemeSettings();
  const isMobile = useIsMobile();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (requests.length === 0) {
    return (
      <Alert severity="info">
        {t ? t('CameraRequests.no_requests_found') : 'No camera footage requests found. Create a new request to get started.'}
      </Alert>
    );
  }

  // Mobile card view renderer
  const renderMobileView = () => (
    <Box>
      {requests.map((request) => (
        <Card key={request.id} sx={{ mb: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t ? t('CameraRequests.request_id') : 'Request ID'}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  #{request.id}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t ? t('CameraRequests.location') : 'Location'}
                </Typography>
                <Typography variant="body1">
                  {request.location?.friendlyName || 'N/A'}
                </Typography>
                {request.location_description && (
                  <Typography variant="caption" color="text.secondary">
                    {request.location_description}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={getStatusIcon(request.status)}
                  label={getStatusLabel(request.status, t)}
                  color={getStatusColor(request.status)}
                  size="small"
                />
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t ? t('CameraRequests.incident_date') : 'Incident Date'}
                </Typography>
                <Typography variant="body2">
                  {format(new Date(request.incident_datetime), 'PPP HH:mm')}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t ? t('CameraRequests.requested_by') : 'Requested By'}
                </Typography>
                <Typography variant="body2">
                  {request.createdByUser?.username || 'Unknown'}
                </Typography>
                {request.createdByUser?.email && (
                  <Typography variant="caption" color="text.secondary">
                    {request.createdByUser.email}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t ? t('CameraRequests.created') : 'Created'}
                </Typography>
                <Typography variant="body2">
                  {format(new Date(request.created_at), 'PPP HH:mm')}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
          <Divider />
          <CardActions>
            <Button
              startIcon={<VisibilityIcon />}
              onClick={() => onViewRequest(request.id)}
              color="primary"
              size="small"
            >
              {t ? t('CameraRequests.view_details') : 'View Details'}
            </Button>
            {isAdmin && (
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  if (onDeleteRequest) {
                    onDeleteRequest(request.id);
                  }
                }}
                title={t ? t('CameraRequests.delete_request') : 'Delete Request'}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </CardActions>
        </Card>
      ))}
    </Box>
  );

  // Desktop view
  if (isMobile) {
    return renderMobileView();
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t ? t('CameraRequests.request_id') : 'ID'}</TableCell>
            <TableCell>{t ? t('CameraRequests.location') : 'Location'}</TableCell>
            <TableCell>{t ? t('CameraRequests.incident_date') : 'Incident Date'}</TableCell>
            <TableCell>{t ? t('CameraRequests.requested_by') : 'Requested By'}</TableCell>
            <TableCell>{t ? t('CameraRequests.current_status') : 'Status'}</TableCell>
            <TableCell>{t ? t('CameraRequests.created') : 'Created'}</TableCell>
            <TableCell align="center">{t ? t('CameraRequests.actions') : 'Actions'}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.map((request) => (
            <TableRow
              key={request.id}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => onViewRequest(request.id)}
            >
              <TableCell>
                <Typography variant="body2" fontWeight="bold">
                  #{request.id}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2">
                  {request.location?.friendlyName || 'N/A'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {request.location_description?.substring(0, 50)}
                  {request.location_description?.length > 50 ? '...' : ''}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2">
                  {format(new Date(request.incident_datetime), 'PPP HH:mm')}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2">
                  {request.createdByUser?.username || 'Unknown'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {request.createdByUser?.email}
                </Typography>
              </TableCell>

              <TableCell>
                <Chip
                  icon={getStatusIcon(request.status)}
                  label={getStatusLabel(request.status, t)}
                  color={getStatusColor(request.status)}
                  size="small"
                />
              </TableCell>

              <TableCell>
                <Typography variant="body2">
                  {format(new Date(request.created_at), 'PPP HH:mm')}
                </Typography>
              </TableCell>

              <TableCell align="center">
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Tooltip title={t ? t('CameraRequests.view_details') : 'View Details'}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewRequest(request.id);
                      }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>

                  {isAdmin && (
                    <Tooltip title={t ? t('CameraRequests.delete_request') : 'Delete Request'}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onDeleteRequest) {
                            onDeleteRequest(request.id);
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CameraRequestsList;
